import { useEffect, useState } from "react";
import { IUser } from "../../../types/user.types";
import { FilterCardItem, LeaveDuration, statusEnum } from "../../../types/common-types";
import { useNavigate } from "react-router-dom";
import { getLeaveRequestCount, getLeaveRequestList } from "../../../apis/performance/leave-request.api";
import StatusCards, { LeaveStats } from "../../company/performance/leave-request/StatusCards";
import { pathNames } from "../../../constants/constants";
import TopBar from "../../common/topbar/TopBar";
import Button from "../../common/button/Button";
import PageLoader from "../../common/loader/PageLoader";
import Pagination from "../../common/pagination/Pagination";
import EmployeeLeaveRequestTable from "./LeaveRequestTable";

export interface ILeaveId {
    _id: string;
    name: string;
}

export interface IEmployeeLeaveRequest {
  _id: string;
  userId: IUser;
  leaveId: ILeaveId;
  startDate: string;
  endDate: string;
  duration: LeaveDuration;
  totalDays: number;
  reason: string;
  status: statusEnum;
  approvedBy: string | null;
  approvedAt: string | null;
  remarks: string;
  createdAt: string;
  updatedAt: string;
}

export const initialEmployeeLeaveRequest: IEmployeeLeaveRequest = {
  _id: "",
  userId: {
    _id: "",
    firstName: "",
    lastName: "",
    profileImage: "",
    role: "",
    userId: ""
  },
  leaveId: {
    _id: "",
    name: ""
  },
  startDate: "",
  endDate: "",
  duration: LeaveDuration.FULL_DAY,
  totalDays: 0,
  reason: "",
  status: statusEnum.PENDING,
  approvedBy: null,
  approvedAt: null,
  remarks: "",
  createdAt: "",
  updatedAt: "",
};

const EmployeeLeaveRequest: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [leaveList, setEmployeeLeaveRequestList] = useState<IEmployeeLeaveRequest[]>([]);

  const [activeCard, setActiveCard] = useState<string>("");

  const [cards, setCards] = useState<FilterCardItem[]>([
    {
      id: "",
      title: "Total",
      count: 0,
      activeColor: "bg-info",
      textColor: "text-info",
      icon: <i className="fa-solid fa-align-justify"></i>,
    },
    {
      id: statusEnum.PENDING,
      title: "Pending",
      count: 0,
      activeColor: "bg-pending",
      textColor: "text-pending",
      icon: <i className="fa-solid fa-hourglass-end"></i>,
    },
    {
      id: statusEnum.APPROVED,
      title: "Approved",
      count: 0,
      activeColor: "bg-success",
      textColor: "text-success",
      icon: <i className="fa-solid fa-user-check"></i>,
    },
    {
      id: statusEnum.REJECTED,
      title: "Rejected",
      count: 0,
      activeColor: "bg-danger",
      textColor: "text-danger",
      icon: <i className="fa-solid fa-user-times"></i>,
    },
  ]);
  // useEffect for get branch
  useEffect(() => {
    fetchEmployeeLeaveRequestList({ page, limit, search, status: activeCard });
    // eslint-disable-next-line
  }, [page, limit, search, activeCard]);

  // get branch list
  const fetchEmployeeLeaveRequestList = async (payload: {
    page: number;
    limit: number;
    search: string;
    status?: statusEnum | string;
  }) => {
    setLoading(true);
    const response = await getLeaveRequestList(payload);
    if (response.success && response.data?.leaves?.length > 0) {
      setEmployeeLeaveRequestList(response.data?.leaves);
      setTotal(response.data?.total);
    } else {
      setEmployeeLeaveRequestList([]);
      setTotal(0);
      setPage(1);
    }
    setLoading(false);
  };

  //call fetch counts
  useEffect(() => {
    fetchLeaveCounts();
    // eslint-disable-next-line
  }, []);

  // fetch leave counts
  const fetchLeaveCounts = async () => {
    const response = await getLeaveRequestCount();
    if (response?.success) {
      updateCards(response?.data);
    }
  };

  // update cards
  const updateCards = (stats: LeaveStats) => {
    setCards((prev) =>
      prev.map((card) => {
        switch (card.id) {
          case "":
            return { ...card, count: stats.total };

          case statusEnum.APPROVED:
            return { ...card, count: stats.approved };

          case statusEnum.REJECTED:
            return { ...card, count: stats.rejected };

          case statusEnum.PENDING:
            return { ...card, count: stats.pending };

          default:
            return card;
        }
      }),
    );
  };

  // handle click add new
  const handleOnAdd = () => {
    navigate(pathNames.ADD_LEAVE_REQUEST);
  };

  // handle search branch
  const handleOnSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handlePageSizeChange = (value: number) => {
    setLimit(value);
    setPage(1);
  };

  // handle Download Excel
  const handleDownloadExcel = async (password: string) => {
    await getLeaveRequestList({
      page,
      limit,
      search,
      status: "",
      isDownload: true,
      password
    });
  };

  return (
    <>
      <TopBar
        title="All Leave Requests"
        actionButtons={
          <Button
            name="Add Leave"
            size="sm"
            onClick={handleOnAdd}
            leftIcon={<i className="fa-solid fa-plus"></i>}
          />
        }
        isSearch
        searchPlaceholder="Search leave..."
        onSearch={handleOnSearch}
        isExcel
        handleDownloadExcel={handleDownloadExcel}
      />
      <div className="content-area flex flex-col gap-3">
        <PageLoader loading={loading} />
        <StatusCards
          cards={cards}
          activeCard={activeCard}
          setActiveCard={setActiveCard}
        />
        <EmployeeLeaveRequestTable
          leaves={leaveList}
          refreshData={() => {
            fetchEmployeeLeaveRequestList({ page, limit, search, status: activeCard });
            fetchLeaveCounts();
          }}
        />
        <Pagination
          totalRecords={total}
          currentPage={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </>
  );
};

export default EmployeeLeaveRequest;
